import { Localized } from "@fluent/react"
import { useLocation, useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { DocumentCreationMode, DocumentNumberingMode } from "../documentsApi"
import {
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    TextField,
    Stack,
    Switch,
    Card,
    CardContent,
    CardHeader,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Box,
} from "@mui/material"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { Help } from "../../dialogs/HelpDialog"
import { If } from "../../../app/If"
import { UrsHeader } from "../../urs/UrsSlice"
import { selectAllUrs } from "../../urs/ursListSlice"
import { AppId, AppTextFieldHandler } from "../../../app/appTypes"
import { LoadURSs } from "../../../app/AppDataLoader"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import SaveIcon from "@mui/icons-material/Save"
import { StructureName } from "../../device/Structure"
import { createRiskAnalysis, RiskAnalysisHeader } from "./riskAnalysisApi"
import { loadSettingsForCompany } from "../../systemSettings/systemDocumentSettings/SystemDocumentSettingsApi"
import { unwrapResult } from "@reduxjs/toolkit"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { StructureTreeSelect } from "../../structure/StructureTreeSelect"
import { selectContextStructure } from "../../structure/contextStructureSlice"
import { selectStructureNodeById } from "../../device/structureSlice"
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { RichTextContainer, tiptapExtensions } from "../../../app/RichTextEditor"
import { useEditor } from "@tiptap/react"
import { CreateRiskAnalysisPayload } from "./riskAnalysisApi"
import { DocumentNumberTextField } from "../controls/DocumentNumberTextField"

const RequiredError = () => (
    <Localized id="field-is-required">
        <span>_Pole jest wymagane</span>
    </Localized>
)

type FormTabs = "purpose" | "description" | "area"


const SectionLabel = ({
    text,
    helpId,
}: {
    text: React.ReactNode;
    helpId?: string;
}) => (
    <Box
        sx={{
            backgroundColor: (theme) => theme.palette.grey[100],
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            px: 1,
            py: 0.75,
        }}
    >
        <Stack direction="row" alignItems="center" gap={0.5}>
            <Box sx={{ color: "text.secondary", display: "flex" }}>
                <ArrowRightIcon fontSize="small" />
            </Box>
            <Box>
                {text}
            </Box>

            {helpId && <Help id={helpId} />}
        </Stack>
    </Box>
)


export const CreateRiskAnalysis = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { state, search } = useLocation()
    const location = useLocation()

    const isSupplierRiskAnalysis = location.pathname.includes("supplier-risk-analysis")
    const params = new URLSearchParams(search)

    const [documentNumber, setDocumentNumber] = useState("")
    const [title, setTitle] = useState("")
    const [language, setLanguage] = useState("PL")
    const [selectedOption, setSelectedOption] = useState<DocumentCreationMode>("Empty")
    const [numberingMode, setNumberingMode] = useState<DocumentNumberingMode>("DECIMAL_HIERARCHICAL")
    const [isIshikawaDiagramEnabled, setIsIshikawaDiagramEnabled] = useState(true)
    const [isTestsEnabled, setIsTestsEnabled] = useState(true)
    const [showForType, setShowForType] = useState(true)

    const { data: contextStructure } = useAppSelector(selectContextStructure)
    const contextStructureId = contextStructure && contextStructure.mode === 'manual' ? contextStructure.structId : ''
    const structure = useAppSelector(state => selectStructureNodeById(state, contextStructureId))
    const [deviceTypeId, setDeviceTypeId] = useState<AppId | undefined>(structure && structure.subStructureIds && structure.subStructureIds.length === 0 ? structure.id : "");

    const editor = useEditor({
        extensions: tiptapExtensions,
        content: '',
    })

    const allUrs = useAppSelector(selectAllUrs)

    useEffect(() => {
        if (selectedOption === "Empty") {
            dispatch(loadSettingsForCompany(`?documentCode=URS`))
                .then(unwrapResult)
                .then((response) => {
                    const numMode = response.find((f) => f.name === "NUMBERING_MODE")
                    if (numMode !== undefined) setNumberingMode(numMode.value as DocumentNumberingMode)
                })
                .catch((error) => console.error(error))
        }
    }, [selectedOption])

    const [selectedUrs, setSelectedUrs] = useState<UrsHeader | undefined>(() => {
        if (state && state[`defaultUrsId`]) {
            const urs = allUrs.find((x) => x.id == state[`defaultUrsId`])
            setSelectedOption("Document")
            return urs ?? undefined
        }
        return undefined
    })

    const [saving, setSaving] = useState(false)
    const [validationErrors, setValidationErrors] = useState(false)
    const [ursBasedOnDialogVisible, setUrsBasedOnDialogVisible] = useState<boolean>(false)

    const [tab, setTab] = useState<FormTabs>("purpose")

    const numberError = validationErrors && documentNumber === ""
    const titleError = validationErrors && title === ""
    const ursError = validationErrors && selectedOption === "Document" && selectedUrs === undefined

    const UrsDocuments = useMemo(() => allUrs, [allUrs, showForType])

    const handleDeviceTypeIdChange = useCallback(structureId => setDeviceTypeId(structureId), [setDeviceTypeId])


    const handleTitleChange: AppTextFieldHandler = useCallback((e) => {
        setTitle(e.target.value)
    }, [])

    const handleDocumentNumberChange: (v: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void =
        useCallback((e) => {
            setDocumentNumber(e.target.value)
        }, [])


    const handleChooseUrsClick = useCallback((newUrs: UrsHeader) => {
        setSelectedUrs(newUrs)
        setUrsBasedOnDialogVisible(false)
    }, [])

    const handleCancelUrsClick = useCallback(() => {
        setUrsBasedOnDialogVisible(false)
    }, [])

    const handleBackButtonClick = useCallback(() => {
        navigate(-1)
    }, [navigate])

    const handleSaveButtonClick = async () => {
        setValidationErrors(true)

        if (documentNumber && title) {
            if (selectedOption === "Document" && selectedUrs === undefined) return

            setSaving(true)
            const docDescription = editor?.getHTML() ?? ''
            try {
                const resultSettings = await dispatch(loadSettingsForCompany(`?documentCode=RiskAnalysis`))
                if (loadSettingsForCompany.fulfilled.match(resultSettings)) {
                    const goodThreshold = resultSettings.payload.find((f) => f.name === "GOOD_THRESHOLD")
                    const warningThreshold = resultSettings.payload.find((f) => f.name === "WARNING_THRESHOLD")

                    const createPayload: CreateRiskAnalysisPayload = {
                        documentType: "RA",
                        creationMode: selectedOption,
                        structureId: deviceTypeId,
                        documentNumber: documentNumber,
                        templateId: "",
                        documentId: selectedUrs?.id || "",
                        title: title,
                        language: language,
                        settings: {
                            numberingMode: numberingMode,
                            goodThreshold: goodThreshold !== undefined ? goodThreshold.value : "0",
                            warningThreshold: warningThreshold !== undefined ? warningThreshold.value : "0",
                            testsEnabled: isTestsEnabled ? "TRUE" : "FALSE",
                        },
                        description: docDescription,
                        purpose: '',
                    }

                    const result = await dispatch(
                        createRiskAnalysis({
                            ...createPayload,
                            isIshikawaDiagramEnabled: selectedOption === "Empty" ? isIshikawaDiagramEnabled : false,
                        })
                    )

                    if (createRiskAnalysis.fulfilled.match(result)) {
                        const newDocument = result.payload as RiskAnalysisHeader
                        dispatch(showSuccess("saved"))
                        navigate(
                            isSupplierRiskAnalysis
                                ? `/supplier-risk-analysis/edit/${newDocument.id}`
                                : `/new-risk-analysis/edit/${newDocument.id}`,
                            { replace: true }
                        )
                    } else {
                        let errorMsg = "error"
                        if (result.payload?.kind === "http") {
                            errorMsg = result.payload.problem?.title ?? errorMsg
                        }
                        dispatch(showError(errorMsg))
                    }
                } else {
                    let errorMsg = "error"
                    if ((resultSettings as any).payload?.kind === "http") {
                        errorMsg = (resultSettings as any).payload.problem?.title ?? errorMsg
                    }
                    dispatch(showError(errorMsg))
                }
            } catch {
                dispatch(showError("error"))
            } finally {
                setSaving(false)
            }
        }
    }

    const columns: GridColDef[] = [
        { field: "ursNo", headerName: "Numer URS", flex: 1, minWidth: 140 },
        {
            field: "title",
            headerName: "Ttitle",
            flex: 1
        },
        {
            field: "deviceTypeId",
            headerName: "Typ urządzenia",
            flex: 2,
            minWidth: 220,
            renderCell: (params) => <StructureName structureId={params.value} />,
        },
        {
            field: "actions",
            headerName: "",
            width: 140,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button variant="outlined" onClick={() => handleChooseUrsClick(params.row)}>
                    <Localized id="select">
                        <span>_Wybierz</span>
                    </Localized>
                </Button>
            ),
        },
    ]

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Card component={Paper} elevation={2}>
                <CardHeader
                    title={<span>Nowa analiza ryzyka</span>}
                    sx={{
                        backgroundColor: (theme) => theme.palette.grey[200],
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                />
                <Divider />
                <CardContent sx={{ pt: 3 }}>
                    <Grid container spacing={2.25}>

                        <Grid item xs={12}>
                            <SectionLabel
                                text={<span>Uzupełnij podstawowe dane dokumentu</span>}
                            />
                        </Grid>
                        {/* Numer + język */}
                        <Grid item xs={12} md={8}>
                            <DocumentNumberTextField
                                fullWidth={true}
                                required={true}
                                value={documentNumber}
                                onChange={handleDocumentNumberChange}
                                label={
                                    <Localized id="document-number">
                                        <span>_Numer dokumentu</span>
                                    </Localized>
                                }
                                documentType={"RA"}
                            />
                            {/* <TextField
                                helperText={numberError && <RequiredError />}
                                required
                                fullWidth
                                value={documentNumber}
                                error={numberError}
                                inputProps={{ maxLength: 50 }}
                                onChange={handleDocumentNumberChange}
                                label={<Localized id="document-number-long"><span>_Numer dokumentu</span></Localized>}
                            /> */}
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel id="app-language-label">
                                    <Localized id="document-language">Język</Localized>
                                </InputLabel>
                                <Select
                                    labelId="app-language-label"
                                    id="app-language"
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                    label={<Localized id="document-language">Język</Localized>}
                                >
                                    <MenuItem value={"PL"}>PL</MenuItem>
                                    <MenuItem value={"EN"}>EN</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Tytuł */}
                        <Grid item xs={12}>
                            <TextField
                                helperText={titleError && <RequiredError />}
                                required
                                fullWidth
                                value={title}
                                error={titleError}
                                onChange={handleTitleChange}
                                label={<Localized id="document-title"><span>_Tytuł</span></Localized>}
                            />
                        </Grid>

                        <Grid item xs={12}>

                            <Stack spacing={1}>
                                <SectionLabel
                                    text={<span>Jak wygenerować dokument?</span>}
                                    helpId="8be28b75-4ca8-4967-8cdb-91a3a1828c2e"
                                />
                                <ToggleButtonGroup
                                    value={selectedOption}
                                    exclusive
                                    size="small"
                                    sx={{
                                        alignSelf: "flex-start",

                                        "& .MuiToggleButton-root.Mui-selected": {
                                            borderColor: "success.main",
                                            borderWidth: 1.5,
                                        }
                                    }}
                                    onChange={(_, v) => {
                                        if (!v) return
                                        setSelectedOption(v)
                                        setValidationErrors(true)

                                        if (v !== "Document") setSelectedUrs(undefined)
                                    }}
                                    fullWidth
                                >
                                    <ToggleButton value="Empty">Pusty</ToggleButton>
                                    <ToggleButton value="Document">URS</ToggleButton>
                                </ToggleButtonGroup>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isIshikawaDiagramEnabled}
                                            onChange={(event) => setIsIshikawaDiagramEnabled(event.target.checked)}
                                            color="primary"
                                            disabled={selectedOption !== "Empty"}
                                        />
                                    }
                                    label={<Localized id="risk-analysis-ishikawa-availability-switch"><span>Diagram Ishikawy</span></Localized>}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isTestsEnabled}
                                            onChange={(event) => setIsTestsEnabled(event.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={<Localized id="risk-analysis-tests-enabled-switch"><span>Testy</span></Localized>}
                                />
                            </Stack>
                        </Grid>

                        {/* URS — label na zewnątrz (sposób 1) */}
                        <If condition={selectedOption == "Document"}>
                            <Grid item xs={12}>
                                <LoadURSs filter="" component={<Skeleton />}>
                                    <Stack spacing={0.75}>
                                        <SectionLabel
                                            text={<span>Wybierz URS na podstawie którego zostanie wygenerowany dokument</span>}
                                            helpId="8be28b75-4ca8-4967-8cdb-91a3a1828c2e"
                                        />

                                        <TextField
                                            value={selectedUrs?.ursNo ?? ""}
                                            fullWidth
                                            required
                                            error={ursError}
                                            helperText={ursError && <RequiredError />}
                                            size="small"
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => setUrsBasedOnDialogVisible(true)}
                                                            edge="end"
                                                        >
                                                            <ArrowDropDownIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Stack>
                                </LoadURSs>
                            </Grid>
                        </If>

                        <If condition={selectedOption == "Empty"}>
                            <Grid item xs={12}>
                                <SectionLabel
                                    text={<span>Wybierz strukturę</span>}
                                />
                                <Box sx={{ mt: 1 }}>
                                    <StructureTreeSelect
                                        defaultStructureId={deviceTypeId}
                                        onStructureSelected={handleDeviceTypeIdChange}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Stack spacing={0.5}>
                                    <SectionLabel
                                        text={<Localized id="risk-analysis-numbering-method-ar"><span>Wybierz sposób numerowania funkcji na AR</span></Localized>}
                                        helpId="7e0300fb-e1cf-4810-aab7-183f5e68d975"
                                    />
                                    <TextField
                                        value={numberingMode}
                                        select
                                        fullWidth
                                        size="small"
                                        onChange={(e) =>
                                            setNumberingMode(e.target.value as DocumentNumberingMode)
                                        }
                                    >
                                        <MenuItem value={"DECIMAL_HIERARCHICAL"}>
                                            <Localized id="urs-hierarchical">
                                                <span>_Hierarchiczne</span>
                                            </Localized>
                                        </MenuItem>
                                        <MenuItem value={"UNIQUE_REQ"}>
                                            <Localized id="urs-unique">
                                                <span>_Unikalne</span>
                                            </Localized>
                                        </MenuItem>
                                    </TextField>
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ mt: 1 }}>
                                    <RichTextContainer label='Opis' editor={editor} />
                                </Box>
                            </Grid>
                        </If>



                        {/* Akcje */}
                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="flex-end" gap={1.25}>

                                <Button
                                    onClick={handleSaveButtonClick}
                                    disabled={saving}
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<SaveIcon />}
                                >
                                    <Localized id="save">
                                        <span>_Zapisz</span>
                                    </Localized>
                                </Button>
                                <Button
                                    onClick={handleBackButtonClick}
                                    variant="outlined"
                                    disabled={saving}
                                >
                                    <Localized id="cancel">Anuluj</Localized>
                                </Button>

                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Dialog URS */}
            <Dialog open={ursBasedOnDialogVisible} fullWidth maxWidth="md">
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <span>Wybierz URS</span>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <div style={{ height: 520, width: "100%" }}>
                        <DataGrid
                            rows={UrsDocuments}
                            columns={columns}
                            slots={{ toolbar: QuickSearchToolbar }}
                            disableRowSelectionOnClick
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelUrsClick}>
                        <Localized id="cancel">
                            <span>_Anuluj</span>
                        </Localized>
                    </Button>
                </DialogActions>
            </Dialog>
        </Container >
    )
}
